<template>
    <div class="ui-page">
        <ui-page-header title="Cestas e lotes" subtitle="Agrupamentos de ativos ofertados nas etapas. Um ativo pode estar numa cesta e num lote, mas não em duas cestas.">
            <v-btn v-if="processId && $allowed('create')" color="primary" depressed @click="openEdit(null)"><v-icon left small>mdi-plus</v-icon>{{ kindText.create }}</v-btn>
        </ui-page-header>

        <div class="ui-list-bar">
            <ui-pills v-model="kind" :items="kindPills" />
        </div>

        <ui-table-card clearable :can-clear="!!search" @clear="search = ''">
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <v-text-field v-model="search" placeholder="Código ou nome" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
            </template>
            <v-data-table :headers="headers" :items="visible" :loading="loading" disable-sort disable-pagination hide-default-footer
                :no-data-text="processId ? kindText.empty : 'Selecione um processo'" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openEdit">
                <template v-slot:[`item.code`]="{ item }"><span class="ui-mono">{{ item.code }}</span></template>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ item.cities.slice(0, 3).join(", ") }}<span v-if="item.cities.length > 3"> e mais {{ item.cities.length - 3 }}</span></div>
                </template>
                <template v-slot:[`item.asset_count`]="{ item }">
                    {{ item.asset_count }}
                    <div v-if="item.blocked_assets" class="ui-cell-sub cp-warn">{{ item.blocked_assets }} {{ item.blocked_assets === 1 ? "indisponível" : "indisponíveis" }}</div>
                </template>
                <template v-slot:[`item.appraisal_total`]="{ item }">
                    <span class="cp-num">{{ formatMoney(item.appraisal_total) }}</span>
                    <div v-if="item.missing_appraisal" class="ui-cell-sub cp-warn">{{ item.missing_appraisal }} sem avaliação</div>
                </template>
                <template v-slot:[`item.stage_numbers`]="{ item }">
                    <span v-if="!item.stage_numbers.length" class="ui-muted">—</span>
                    <span v-else>{{ item.stage_numbers.map((number) => `Etapa ${number}`).join(", ") }}</span>
                </template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
        </ui-table-card>

        <v-dialog v-model="edit.open" max-width="820" persistent scrollable>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ edit.id ? `${kindLabel} ${edit.form.code}` : kindText.create }}</h3>
                        <p class="ui-section-description">
                            {{ edit.selected.length }} {{ edit.selected.length === 1 ? "ativo" : "ativos" }} · avaliação {{ formatMoney(selectedTotal) }}
                            <span v-if="edit.locked" class="cp-warn"> · ofertado em etapa: os ativos não podem mudar</span>
                        </p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="3"><v-text-field v-model="edit.form.code" label="Código" outlined dense hide-details="auto" maxlength="30" :placeholder="edit.id ? '' : 'Automático'"></v-text-field></v-col>
                        <v-col cols="9"><v-text-field v-model="edit.form.name" label="Nome" outlined dense hide-details="auto" maxlength="255"></v-text-field></v-col>
                        <v-col cols="12"><v-textarea v-model="edit.form.description" label="Descrição (vitrine)" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                    </v-row>
                    <div class="d-flex align-center flex-wrap mt-4 mb-2" style="gap: 12px;">
                        <div class="ui-label mb-0">Ativos</div>
                        <v-spacer></v-spacer>
                        <v-checkbox v-model="edit.onlyFree" dense hide-details label="Só os livres" class="mt-0"></v-checkbox>
                        <v-text-field v-model="edit.search" placeholder="Buscar ativo" dense outlined hide-details prepend-inner-icon="mdi-magnify" style="max-width: 260px;"></v-text-field>
                    </div>
                    <div class="cp-pick-list">
                        <div v-for="asset in pickable" :key="asset.id" class="cp-pick">
                            <v-checkbox :input-value="edit.selected.includes(asset.id)" :disabled="edit.locked || !!asset.takenBy" dense hide-details @change="toggle(asset.id)"></v-checkbox>
                            <div class="cp-pick__text">
                                <div class="ui-cell-main"><span class="ui-mono">{{ asset.code }}</span> · {{ asset.title }}</div>
                                <div class="ui-cell-sub">
                                    {{ asset.city ? `${asset.city}${asset.state ? `/${asset.state}` : ""}` : "sem município" }}
                                    <span v-if="asset.takenBy" class="cp-warn"> · já em {{ asset.takenBy }}</span>
                                    <span v-if="asset.status !== 'available'" class="cp-warn"> · {{ asset.status_label.toLowerCase() }}</span>
                                </div>
                            </div>
                            <div class="cp-pick__value">{{ formatMoney(asset.appraisal_value) }}</div>
                        </div>
                        <div v-if="!pickable.length" class="ui-empty">Nenhum ativo</div>
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn v-if="edit.id && $allowed('delete')" text class="ui-btn-danger" :disabled="edit.saving" @click="remove">Excluir</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed(edit.id ? 'edit' : 'create')" color="primary" depressed :loading="edit.saving" :disabled="!String(edit.form.name || '').trim()" @click="save">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, GROUP_KIND_PILLS, formatMoney, notifyError, notifySuccess } from "@/components/competitive/competitive.util";

const KIND_LABELS = { basket: "Cesta", lot: "Lote", block: "Bloco único" };
const KIND_TEXTS = {
    basket: { create: "Nova cesta", empty: "Nenhuma cesta cadastrada", saved: "Cesta salva." },
    lot: { create: "Novo lote", empty: "Nenhum lote cadastrado", saved: "Lote salvo." },
    block: { create: "Novo bloco único", empty: "Nenhum bloco único cadastrado", saved: "Bloco único salvo." },
};

export default {
    name: "CompetitiveGroupList",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data() {
        return {
            kind: ["basket", "lot", "block"].includes(this.$route.query.tipo) ? this.$route.query.tipo : "basket",
            items: [],
            assets: [],
            loading: false,
            search: "",
            edit: { open: false, id: null, saving: false, locked: false, search: "", onlyFree: false, selected: [], form: {} },
            headers: [
                { text: "Código", value: "code", width: 90 },
                { text: "Nome", value: "name" },
                { text: "Ativos", value: "asset_count", width: 130 },
                { text: "Avaliação", value: "appraisal_total", width: 190 },
                { text: "Ofertado em", value: "stage_numbers", width: 150 },
                { text: "", value: "actions", align: "end", width: 40 },
            ],
        };
    },
    computed: {
        kindLabel() {
            return KIND_LABELS[this.kind];
        },
        kindText() {
            return KIND_TEXTS[this.kind];
        },
        kindPills() {
            return GROUP_KIND_PILLS.map((pill) => ({ ...pill, count: this.items.filter((item) => item.kind === pill.value).length }));
        },
        visible() {
            const term = this.search.trim().toLowerCase();
            return this.items.filter((item) => item.kind === this.kind && (!term || [item.code, item.name].some((value) => String(value || "").toLowerCase().includes(term))));
        },
        // Ativos com o agrupamento do mesmo tipo que ja os tem (fora o que esta aberto).
        assetRows() {
            return this.assets.map((asset) => {
                const other = asset.groups.find((group) => group.kind === this.kind && group.id !== this.edit.id);
                return { ...asset, takenBy: other ? other.code : null };
            });
        },
        pickable() {
            const term = this.edit.search.trim().toLowerCase();
            return this.assetRows.filter((asset) => (!this.edit.onlyFree || !asset.takenBy || this.edit.selected.includes(asset.id))
                && (!term || [asset.code, asset.title, asset.city].some((value) => String(value || "").toLowerCase().includes(term))));
        },
        selectedTotal() {
            const cents = this.assets.filter((asset) => this.edit.selected.includes(asset.id)).reduce((total, asset) => total + Math.round((Number(asset.appraisal_value) || 0) * 100), 0);
            return cents / 100;
        },
    },
    watch: {
        kind(value) {
            this.$router.replace({ query: { ...this.$route.query, tipo: value } }).catch(() => {});
        },
    },
    methods: {
        formatMoney,
        onProcess() {
            this.load();
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const [groups, assets] = await Promise.all([
                    axios.get(`${API}/processes/${this.processId}/groups`),
                    axios.get(`${API}/processes/${this.processId}/assets`),
                ]);
                this.items = groups.data.content;
                this.assets = assets.data.content.data;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        openEdit(item) {
            this.edit = {
                open: true,
                id: item ? item.id : null,
                saving: false,
                locked: !!(item && item.stage_numbers.length),
                search: "",
                onlyFree: !item,
                selected: item ? item.assets.map((asset) => asset.id) : [],
                form: item ? { code: item.code, name: item.name, description: item.description || "" } : { code: "", name: "", description: "" },
            };
        },
        toggle(id) {
            const index = this.edit.selected.indexOf(id);
            if (index >= 0) this.edit.selected.splice(index, 1);
            else this.edit.selected.push(id);
        },
        async save() {
            this.edit.saving = true;
            try {
                const payload = { ...this.edit.form };
                if (!this.edit.locked) payload.asset_ids = this.edit.selected;
                if (this.edit.id) await axios.patch(`${API}/groups/${this.edit.id}`, payload);
                else await axios.post(`${API}/processes/${this.processId}/groups`, { ...payload, kind: this.kind });
                notifySuccess(this, this.kindText.saved);
                this.edit.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
            }
        },
        async remove() {
            const confirmed = await this.$dialog.confirm({
                title: `Excluir ${this.kindLabel.toLowerCase()}`,
                text: `${this.edit.form.code} sai do processo; os ativos continuam cadastrados. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/groups/${this.edit.id}`);
                this.edit.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
